// src/pages/products/ProductList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ProductForm from "./ProductForm";
import API_BASE_URL from "../../utils/config";

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [expanded, setExpanded] = useState({});
  const [batchesByProduct, setBatchesByProduct] = useState({});

  // search
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  // loading/errors
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  // quick filters
  const [fLowStock, setFLowStock] = useState(false);
  const [fNoStock, setFNoStock] = useState(false);
  const [fHasExpired, setFHasExpired] = useState(false);
  const [fCategory, setFCategory] = useState("");

  // expiring soon
  const [fExpSoon, setFExpSoon] = useState(false);
  const [expDays, setExpDays] = useState(30);

  // NEW — top selling
  const [fTopSelling, setFTopSelling] = useState(false);
  const [topDays, setTopDays] = useState(30);

  const [expiryIndex, setExpiryIndex] = useState({});
  const [buildingExpiryIdx, setBuildingExpiryIdx] = useState(false);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ---------- utils ----------
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const money = (v) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toNum(v));

  const normalizeBatches = (payload) => {
    let arr = [];

    if (Array.isArray(payload)) arr = payload;
    else if (Array.isArray(payload?.data)) arr = payload.data;
    else if (payload?.data && typeof payload.data === "object") arr = [payload.data];
    else if (Array.isArray(payload?.rows)) arr = payload.rows;
    else if (payload && typeof payload === "object") arr = [payload];

    return arr.map((b) => ({
      id: b.id ?? b.batch_id,
      batch_no: b.batch_no ?? b.batch_number ?? "-",
      expiry: b.expiry ?? b.expiry_date ?? null,
      purchase_date: b.purchase_date ?? b.purchasedate ?? null,
      quantity: b.quantity ?? 0,
      current_stock: toNum(b.current_stock ?? b.stock ?? 0),
      purchase_rate: toNum(b.purchase_rate ?? 0),
      sale_rate: toNum(b.sale_rate ?? 0),
      mrp: toNum(b.mrp ?? 0),
    }));
  };

  const withinNDays = (d, n) => {
    if (!d) return false;
    const now = new Date();
    const ms = new Date(d).getTime() - now.getTime();
    return ms >= 0 && ms <= n * 86400000;
  };

  // ---------- data fetch ----------
  const fetchProducts = async (q = "") => {
    try {
      setLoading(true);
      setFetchError("");

      let url = `${API_BASE_URL}/api/products`;
      const params = new URLSearchParams();

      if (q) params.append("q", q);
      if (fTopSelling) {
        params.append("top_selling", 1);
        params.append("days", topDays);
      }

      if (params.toString()) url += `?${params.toString()}`;

      const res = await axios.get(url, authHeader);
      setProducts(res.data || []);
    } catch (err) {
      console.error(err);
      setFetchError("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchProducts(debounced);
  }, [debounced, fTopSelling, topDays]);

  // ---------- batch expand ----------
  const toggleExpand = (productId) => {
    setExpanded((prev) => {
      const open = !prev[productId];

      if (open && !batchesByProduct[productId]) {
        (async () => {
          setBatchesByProduct((p) => ({
            ...p,
            [productId]: { loading: true, error: null, rows: [] },
          }));

          try {
            const res = await axios.get(
              `${API_BASE_URL}/api/batches/productId?productId=${productId}`,
              authHeader
            );
            const rows = normalizeBatches(res.data);
            setBatchesByProduct((p) => ({
              ...p,
              [productId]: { loading: false, error: null, rows },
            }));

            const earliest =
              rows
                .map((r) => (r.expiry ? new Date(r.expiry) : null))
                .filter(Boolean)
                .sort((a, b) => a - b)[0] || null;

            setExpiryIndex((idx) => ({ ...idx, [productId]: earliest }));
          } catch (err) {
            setBatchesByProduct((p) => ({
              ...p,
              [productId]: { loading: false, error: "Failed to load batches", rows: [] },
            }));
          }
        })();
      }

      return { ...prev, [productId]: open };
    });
  };

  // ---------- small UI ----------
  const Pill = ({ active, onClick, children, tone = "gray" }) => {
    const tones = {
      gray: "bg-gray-100 text-gray-700",
      blue: "bg-blue-50 text-blue-700",
      orange: "bg-orange-50 text-orange-700",
      green: "bg-emerald-50 text-emerald-700",
      red: "bg-rose-50 text-rose-700",
    };
    return (
      <button
        onClick={onClick}
        className={`px-3 py-1 text-sm rounded-full border ${
          active ? "border-blue-500 bg-blue-100 text-blue-700" : tones[tone]
        }`}
      >
        {children}
      </button>
    );
  };

  // ---------- filter apply ----------
  const filteredProducts = useMemo(() => {
    let list = products;

    if (fLowStock) list = list.filter((p) => (p.stock_not_expired ?? 0) < 10);
    if (fNoStock) list = list.filter((p) => (p.stock_not_expired ?? 0) === 0);
    if (fHasExpired) list = list.filter((p) => (p.stock_expired ?? 0) > 0);

    if (fCategory) list = list.filter((p) => p.category_name === fCategory);

    if (fExpSoon) {
      list = list.filter((p) => {
        const seeded = p.next_expiry || p.min_expiry || p.earliest_expiry;
        const d = seeded ? new Date(seeded) : expiryIndex[p.id];
        return withinNDays(d, expDays);
      });
    }

    return list;
  }, [
    products,
    fLowStock,
    fNoStock,
    fHasExpired,
    fCategory,
    fExpSoon,
    expDays,
    expiryIndex,
  ]);

  // ---------- batch section ----------
  const renderBatchSection = (productId) => {
    const info = batchesByProduct[productId];

    if (!info) return null;
    if (info.loading) return <div>Loading batches...</div>;
    if (info.error) return <div className="text-red-600">{info.error}</div>;

    const rows = info.rows || [];

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="p-2 text-left">Batch</th>
              <th className="p-2 text-left">Expiry</th>
              <th className="p-2 text-right">Stock</th>
              <th className="p-2 text-right">MRP</th>
              <th className="p-2 text-right">Sale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="border-t">
                <td className="p-2">{b.batch_no}</td>
                <td className="p-2">{b.expiry ? b.expiry : "-"}</td>
                <td className="p-2 text-right">{b.current_stock}</td>
                <td className="p-2 text-right">{money(b.mrp)}</td>
                <td className="p-2 text-right">{money(b.sale_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------- render ----------
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Products</h1>

        <button
          onClick={() => {
            setEditingProduct(null);
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow"
        >
          + Add Product
        </button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 items-center">
        <Pill active={fLowStock} onClick={() => setFLowStock(!fLowStock)}>
          Low stock (&lt; 10)
        </Pill>

        <Pill active={fNoStock} onClick={() => setFNoStock(!fNoStock)}>
          No stock
        </Pill>

        <Pill active={fHasExpired} onClick={() => setFHasExpired(!fHasExpired)} tone="red">
          Has expired stock
        </Pill>

        <Pill active={fExpSoon} onClick={() => setFExpSoon(!fExpSoon)} tone="orange">
          Expiring within
        </Pill>

        {fExpSoon && (
          <select
            className="border rounded px-2 py-1"
            value={expDays}
            onChange={(e) => setExpDays(Number(e.target.value))}
          >
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        )}

        {/* NEW — Top Selling */}
        <Pill
          active={fTopSelling}
          onClick={() => setFTopSelling(!fTopSelling)}
          tone="green"
        >
          Top Selling
        </Pill>

        {fTopSelling && (
          <select
            className="border rounded px-2 py-1"
            value={topDays}
            onChange={(e) => setTopDays(Number(e.target.value))}
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 3 months</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
          </select>
        )}

        {/* category */}
        <select
          className="border rounded px-2 py-1"
          value={fCategory}
          onChange={(e) => setFCategory(e.target.value)}
        >
          <option value="">All categories</option>
          {products
            .filter((p) => p.category_name)
            .map((p) => p.category_name)
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort()
            .map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
        </select>

        {/* search */}
        <input
          className="border rounded px-3 py-1 w-56"
          placeholder="Search product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Products Table */}
      <div className="rounded-xl border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3 text-left font-medium">Name</th>
                <th className="p-3 text-right font-medium">MRP</th>
                <th className="p-3 text-right font-medium">Stock</th>
                <th className="p-3 text-center font-medium">Batches</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredProducts.map((p) => (
                <React.Fragment key={p.id}>
                  <tr className="border-t hover:bg-gray-50 transition">
                    <td className="p-3">{p.name}</td>
                    <td className="p-3 text-right">{money(p.mrp)}</td>
                    <td className="p-3 text-right">{p.stock_not_expired ?? 0}</td>

                    <td className="p-3 text-center">
                      <button
                        className="text-blue-600 underline"
                        onClick={() => toggleExpand(p.id)}
                      >
                        {expanded[p.id] ? "Hide" : "View"}
                      </button>
                    </td>

                    <td className="p-3 text-right">
                      <button
                        className="px-3 py-1 bg-yellow-500 text-white rounded"
                        onClick={() => {
                          setEditingProduct(p);
                          setShowForm(true);
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>

                  {expanded[p.id] && (
                    <tr>
                      <td colSpan={5} className="bg-gray-50 p-4 border-t">
                        {renderBatchSection(p.id)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          onClose={() => setShowForm(false)}
          refresh={() => fetchProducts()}
        />
      )}
    </div>
  );
}
